import React from 'react';
import {DragAndDropNames} from '../../Enums';
import {DragAndDropState, useDragAndDropState} from '../Context/DragAndDrop/DragAndDropContext';

interface DropProps {
  draggable?: boolean;
  onDragEnter?: (event: React.DragEvent<HTMLElement>) => void;
  onDragOver?: (event: React.DragEvent<HTMLElement>) => void;
  onDragLeave?: (event: React.DragEvent<HTMLElement>) => void;
  onDrop?: (event: React.DragEvent<HTMLElement>) => void;
}

export type DragAndDropProps = {
  name: DragAndDropNames;
  dropEffect?: 'none' | 'copy' | 'link' | 'move';
  dragEnter?: (event: React.DragEvent<HTMLElement>, state: DragAndDropState) => void;
  dragOver?: (event: React.DragEvent<HTMLElement>, state: DragAndDropState) => boolean;
  dragLeave?: (event: React.DragEvent<HTMLElement>, state: DragAndDropState) => void;
  drop?: (event: React.DragEvent<HTMLElement>, state: DragAndDropState) => void;
  children: React.ReactElement | React.ReactElement[];
};

const Drop = ({
  name = DragAndDropNames.NONE,
  dropEffect = 'none',
  dragEnter,
  dragOver,
  dragLeave,
  drop,
  children,
}: DragAndDropProps): React.ReactElement => {
  const dragAndDrop = useDragAndDropState();

  /**
   * Check if drop events are allowed. Can be used to restrict the draggable item being dropped in certain places.
   * Setting the name to None will accept all drop events.
   *
   * @returns True if drop is allowed.
   */
  const isDropAllowed = (): boolean => {
    return DragAndDropNames.NONE === dragAndDrop.state.name || name === dragAndDrop.state.name;
  };

  /**
   * Wrapper that provides some default functionality that fires on dragEnter.
   * Fires the prop dragEnter if a valid function is passed.
   *
   * @param event React DragEvent object.
   */
  const onDragEnter = (event: React.DragEvent<HTMLElement>): void => {
    if (!isDropAllowed()) {
      return;
    }

    event.preventDefault();

    if (typeof dragEnter === 'function') {
      dragEnter(event, dragAndDrop.state);
    }
  };

  /**
   * Wrapper that provides some default functionality that fires on dragLeave.
   * Fires the prop dragLeave if a valid function is passed.
   *
   * @param event React DragEvent object.
   */
  const onDragLeave = (event: React.DragEvent<HTMLElement>): void => {
    if (!isDropAllowed()) {
      return;
    }

    if (typeof dragLeave === 'function') {
      dragLeave(event, dragAndDrop.state);
    }
  };

  /**
   * Wrapper that provides some default functionality that fires on dragOver.
   * Fires the prop dragOver if a valid function is passed.
   *
   * @param event React DragEvent object.
   */
  const onDragOver = (event: React.DragEvent<HTMLElement>): void => {
    let actionValid = true;

    if (!isDropAllowed()) {
      return;
    }

    // Required to enable drop on target.
    event.preventDefault();

    // Determines what icon appears.
    event.dataTransfer.dropEffect = dropEffect;

    if (typeof dragOver === 'function') {
      actionValid = dragOver(event, dragAndDrop.state);
    }

    if (!actionValid) {
      event.dataTransfer.dropEffect = 'none';
    }
  };

  /**
   * Wrapper that provides some default functionality that fires on drop.
   *
   * @param event React DragEvent object.
   */
  const onDrop = (event: React.DragEvent<HTMLElement>): void => {
    if (!isDropAllowed()) {
      return;
    }

    if (typeof drop === 'function') {
      drop(event, dragAndDrop.state);
    }

    // Required to enable drop on target.
    event.preventDefault();
  };

  const dropTarget = React.Children.map(
    children,
    (child: React.ReactElement): React.ReactElement => {
      const dropProps: DropProps = {};

      dropProps.onDragEnter = onDragEnter;
      dropProps.onDragOver = onDragOver;
      dropProps.onDragLeave = onDragLeave;
      dropProps.onDrop = onDrop;

      return React.cloneElement(child, {
        ...child.props,
        ...dropProps,
      });
    }
  );

  return <>{dropTarget}</>;
};

export default Drop;
